// Sporteurope embedded player shell — entry point.
// Loads the WebPlayer from CDN for a UUID parsed from the URL and renders
// local overlays (consent wall, paywall, login, errors). Forwards analytics
// events to GA4 (consent-gated, see ./lib/ga.js). Makes no own API calls —
// the WebPlayer fetches the asset itself; NO_EMBED enforcement is expected
// server-side and surfaces as a player ERROR event.

import {ENV, ENV_KEY} from './lib/env.js';
import { LANG } from './lib/i18n.js';
import { showError, showInfo, showPaywall, showLogin, showConsentWall, clearRoot } from './lib/ui.js';
import { watchGaConsent } from './lib/ga.js';

const root = document.getElementById('player');
const params = new URLSearchParams(location.search);
const assetId = location.pathname.match(
  /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i,
)?.[1] ?? null;

if (!assetId) {
  showError('no_asset_title', 'no_asset_text');
} else {
  boot().catch((err) => err?.message === 'cmp-timeout'
    ? showError('consent_title', 'consent_text')
    : showError('error_title',   'error_text'));
}

async function boot() {
  watchGaConsent();
  await waitForCmp();
  try { await window.__ucCmp.closeCmp(); } catch {}

  // PLUS path is a deliberate sackgasse: the link opens sporteurope.tv in a
  // new tab; the iframe just keeps showing the wall until consent is given.
  const tcString = (await currentTcString()) ?? await new Promise(showConsentWall);

  loadStylesheet(ENV.webplayerStyleUrl);
  const { SporteuropePlayer, PLAYER_EVENT, ERROR_REASON } = await import(
    /* @vite-ignore */ ENV.webplayerScriptUrl
  );

  clearRoot(); // WebPlayer appends to its container; clear the wall first
  const player = new SporteuropePlayer({
    container: root,
    debug: ENV_KEY === 'dev',
    assetId,
    autoplay: params.get('autoplay') === 'true',
    muted:    params.get('muted')    === 'true',
    controls: params.get('controls') !== 'false',
    language: LANG,
    ads: { tc_string: tcString },
  });
  player.onLog?.((e) => {if (ENV_KEY === 'dev') console.log(e)});

  // Forward analytics events to GA4. Events fired before gtag.js loads are
  // buffered in dataLayer by ./lib/ga.js and replayed later.
  player.on(PLAYER_EVENT.ANALYTICS, ({ event, ...rest } = {}) => {
    if (event) window.gtag('event', event, rest);
  });

  // Map each error reason we surface to an overlay. DEVICE_LIMIT is silent
  // (the WebPlayer renders its own UI for it). Anything else → generic.
  const onError = {
    [ERROR_REASON.LOGIN_REQUIRED]:    showLogin,
    [ERROR_REASON.SESSION_EXPIRED]:   showLogin,
    [ERROR_REASON.PAYWALL]:           () => showPaywall(assetId),
    [ERROR_REASON.GEO_BLOCKED]:       () => showInfo('geo_title', 'geo_text'),
    [ERROR_REASON.NOT_FOUND]:         () => showError('not_found_title', 'not_found_text'),
    [ERROR_REASON.MISSING_TC_STRING]: () => showError('consent_title', 'consent_text'),
    [ERROR_REASON.AD_BLOCKER]:        () => showInfo('adblocker_title', 'adblocker_text'),
    [ERROR_REASON.DEVICE_LIMIT]:      () => {},
  };
  const fallback = () => showError('error_title', 'error_text');
  player.on(PLAYER_EVENT.ERROR, (err) => (onError[err?.reason] ?? fallback)());

  // Keep tc_string in sync with later consent changes (mirrors
  // sporteurope-frontend setupTcfListener). COUNTDOWN_REQUIRED is handled by
  // the WebPlayer's own CountdownOverlay — no shell-side rendering needed.
  window.__tcfapi?.('addEventListener', 2, (tcData, ok) => {
    if (ok && tcData?.eventStatus === 'useractioncomplete') {
      player.setSetting?.('tc_string', tcData.tcString || '');
    }
  });
}

function loadStylesheet(href) {
  if (!href || document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

function waitForCmp() {
  return new Promise((resolve, reject) => {
    if (window.__ucCmp) return resolve();
    window.addEventListener('UC_UI_INITIALIZED', resolve, { once: true });
    setTimeout(() => reject(new Error('cmp-timeout')), 8000);
  });
}

// Current tcString if the user has already given full consent — `null`
// otherwise so the caller knows the wall is needed.
async function currentTcString() {
  const det = await window.__ucCmp.getConsentDetails().catch(() => null);
  return det && !det.consent.required && det.consent.status === 'ALL_ACCEPTED'
    ? (det.consent.tcString || '')
    : null;
}
